// Piece definitions in their 4 rotation states, expressed as offsets from a 4×4 bounding box anchor.
// SRS wall-kick tables from the official Tetris Guideline.

import type { PieceType, Rotation } from '../types';

export type Cells = readonly (readonly [number, number])[];

// For each piece, an array of 4 rotation states. Each state is a list of [dx, dy] offsets
// from the piece anchor (top-left of bounding box). y grows downward.
type PieceShape = readonly [Cells, Cells, Cells, Cells];

// I tetromino uses a 4×4 box. Rotation states per SRS.
const I_SHAPES: PieceShape = [
  [[0,1],[1,1],[2,1],[3,1]],
  [[2,0],[2,1],[2,2],[2,3]],
  [[0,2],[1,2],[2,2],[3,2]],
  [[1,0],[1,1],[1,2],[1,3]],
];

// O — never rotates effectively, but we still keep four identical states.
const O_SHAPES: PieceShape = [
  [[1,0],[2,0],[1,1],[2,1]],
  [[1,0],[2,0],[1,1],[2,1]],
  [[1,0],[2,0],[1,1],[2,1]],
  [[1,0],[2,0],[1,1],[2,1]],
];

// T, S, Z, J, L use a 3×3 box (anchor still in 4×4 — we use the top-left of a 3×3 region offset by 0).
const T_SHAPES: PieceShape = [
  [[1,0],[0,1],[1,1],[2,1]],
  [[1,0],[1,1],[2,1],[1,2]],
  [[0,1],[1,1],[2,1],[1,2]],
  [[1,0],[0,1],[1,1],[1,2]],
];

const S_SHAPES: PieceShape = [
  [[1,0],[2,0],[0,1],[1,1]],
  [[1,0],[1,1],[2,1],[2,2]],
  [[1,1],[2,1],[0,2],[1,2]],
  [[0,0],[0,1],[1,1],[1,2]],
];

const Z_SHAPES: PieceShape = [
  [[0,0],[1,0],[1,1],[2,1]],
  [[2,0],[1,1],[2,1],[1,2]],
  [[0,1],[1,1],[1,2],[2,2]],
  [[1,0],[0,1],[1,1],[0,2]],
];

const J_SHAPES: PieceShape = [
  [[0,0],[0,1],[1,1],[2,1]],
  [[1,0],[2,0],[1,1],[1,2]],
  [[0,1],[1,1],[2,1],[2,2]],
  [[1,0],[1,1],[0,2],[1,2]],
];

const L_SHAPES: PieceShape = [
  [[2,0],[0,1],[1,1],[2,1]],
  [[1,0],[1,1],[1,2],[2,2]],
  [[0,1],[1,1],[2,1],[0,2]],
  [[0,0],[1,0],[1,1],[1,2]],
];

const SHAPES: Record<PieceType, PieceShape> = {
  I: I_SHAPES,
  O: O_SHAPES,
  T: T_SHAPES,
  S: S_SHAPES,
  Z: Z_SHAPES,
  J: J_SHAPES,
  L: L_SHAPES,
};

export function shapeOf(type: PieceType, rotation: Rotation): Cells {
  return SHAPES[type][rotation];
}

// SRS wall-kick tables. Index by [from][to] transition.
// Each entry is a list of (dx, dy) offsets to test in order. Note y is inverted vs. classic SRS docs
// because in our coordinate system y grows downward, so positive dy in SRS docs (which uses y-up)
// becomes negative dy here.
type KickTable = Record<string, readonly (readonly [number, number])[]>;

// Helper: convert from classic SRS [dx, dy_up] to our [dx, dy_down]
const k = (...pairs: ReadonlyArray<readonly [number, number]>): ReadonlyArray<readonly [number, number]> =>
  pairs.map(([dx, dy]) => [dx, -dy] as const);

// JLSTZ kicks
const JLSTZ_KICKS: KickTable = {
  '0->1': k([0,0],[-1,0],[-1,1],[0,-2],[-1,-2]),
  '1->0': k([0,0],[1,0],[1,-1],[0,2],[1,2]),
  '1->2': k([0,0],[1,0],[1,-1],[0,2],[1,2]),
  '2->1': k([0,0],[-1,0],[-1,1],[0,-2],[-1,-2]),
  '2->3': k([0,0],[1,0],[1,1],[0,-2],[1,-2]),
  '3->2': k([0,0],[-1,0],[-1,-1],[0,2],[-1,2]),
  '3->0': k([0,0],[-1,0],[-1,-1],[0,2],[-1,2]),
  '0->3': k([0,0],[1,0],[1,1],[0,-2],[1,-2]),
  // 180s — common modern extension (Tetr.io style: just attempt center + small offsets).
  '0->2': k([0,0],[0,1],[1,1],[-1,1],[1,0],[-1,0]),
  '2->0': k([0,0],[0,-1],[-1,-1],[1,-1],[-1,0],[1,0]),
  '1->3': k([0,0],[1,0],[1,2],[1,1],[0,2],[0,1]),
  '3->1': k([0,0],[-1,0],[-1,2],[-1,1],[0,2],[0,1]),
};

// I-piece kicks (SRS variant)
const I_KICKS: KickTable = {
  '0->1': k([0,0],[-2,0],[1,0],[-2,-1],[1,2]),
  '1->0': k([0,0],[2,0],[-1,0],[2,1],[-1,-2]),
  '1->2': k([0,0],[-1,0],[2,0],[-1,2],[2,-1]),
  '2->1': k([0,0],[1,0],[-2,0],[1,-2],[-2,1]),
  '2->3': k([0,0],[2,0],[-1,0],[2,1],[-1,-2]),
  '3->2': k([0,0],[-2,0],[1,0],[-2,-1],[1,2]),
  '3->0': k([0,0],[1,0],[-2,0],[1,-2],[-2,1]),
  '0->3': k([0,0],[-1,0],[2,0],[-1,2],[2,-1]),
  '0->2': k([0,0],[0,1],[0,-1],[1,0],[-1,0]),
  '2->0': k([0,0],[0,-1],[0,1],[-1,0],[1,0]),
  '1->3': k([0,0],[0,1],[0,-1],[1,0],[-1,0]),
  '3->1': k([0,0],[0,-1],[0,1],[-1,0],[1,0]),
};

// O has no kicks.
const O_KICKS: KickTable = {
  '0->1': [[0, 0]], '1->0': [[0, 0]], '1->2': [[0, 0]], '2->1': [[0, 0]],
  '2->3': [[0, 0]], '3->2': [[0, 0]], '3->0': [[0, 0]], '0->3': [[0, 0]],
  '0->2': [[0, 0]], '2->0': [[0, 0]], '1->3': [[0, 0]], '3->1': [[0, 0]],
};

export function kicks(type: PieceType, from: Rotation, to: Rotation): ReadonlyArray<readonly [number, number]> {
  const key = `${from}->${to}`;
  const table = type === 'I' ? I_KICKS : type === 'O' ? O_KICKS : JLSTZ_KICKS;
  return table[key] ?? [[0, 0]];
}

// Spawn position: bounding-box top-left in board coordinates.
// Per guideline, pieces spawn straddling columns 4 and 5, top of visible field.
// In our coordinate system, the visible field starts at y = BOARD_BUFFER_HEIGHT.
import { BOARD_BUFFER_HEIGHT } from '../config';

export function spawnPosition(type: PieceType): { x: number; y: number } {
  // Choose anchor y so the topmost cell of the piece lands exactly on the first visible row.
  // I has topmost cells at dy=1, so anchor sits one row above the visible field.
  // O / T / S / Z / J / L have topmost cells at dy=0, so anchor is the first visible row.
  if (type === 'I') return { x: 3, y: BOARD_BUFFER_HEIGHT - 1 };
  return { x: 3, y: BOARD_BUFFER_HEIGHT };
}
